import React from 'react';
import { CARD_DENSITIES, type CardDensity } from '@troia-v3/types';
import { ViewToggle } from '@/shared/components/ui/ViewToggle';
import type { ViewToggleMode } from '@/shared/components/ui/ViewToggle';

/**
 * Os três tamanhos, indexados pela densidade. Ícones escolhidos entre os nomes
 * presentes nos QUATRO packs (feather/lucide/heroicons/tabler) — nome ausente
 * em um pack cai no fallback 'alert-circle' e vira exclamação.
 * `list` é evitado de propósito: já é o ícone do modo "Lista" do ViewToggle de
 * visualização, que fica ao lado deste na mesma toolbar.
 *
 * É `Record`, e não array, porque a ordem de exibição vem sempre de uma LISTA
 * de densidades (`CARD_DENSITIES`, ou o subconjunto que a Tarefa 19 passa em
 * `options`), e essa lista é mapeada para os modos lendo daqui.
 */
const DENSITY_MODES: Record<CardDensity, ViewToggleMode<CardDensity>> = {
  detailed: { id: 'detailed', icon: 'file-text', label: 'Detalhes', tooltip: 'Detalhes — mais informação por card' },
  normal: { id: 'normal', icon: 'layers', label: 'Normal', tooltip: 'Normal — equilíbrio de informação' },
  compact: { id: 'compact', icon: 'menu', label: 'Compacto', tooltip: 'Compacto — uma linha por card' },
};

export interface CardDensitySelectorProps {
  value: CardDensity;
  onChange: (density: CardDensity) => void;
  /**
   * Classe extra no wrapper (pass-through). O colapso da legenda vem do
   * ViewToggle (padrão do DS 2026-08-04: `hidden 2xl:@tb:inline`) — os antigos
   * ganchos `ds-dens`/`ds-dens-m` foram aposentados.
   */
  className?: string;
  /**
   * Subconjunto de densidades exibido. Ausente = `CARD_DENSITIES` (as três).
   * Existe para o caso em que um módulo não tem corpo de card para alguma
   * posição — uma pill que não muda nada lê como controle quebrado. O Helpdesk
   * usava isso enquanto o card 07 não existia; hoje passa as três.
   */
  options?: readonly CardDensity[];
  /** Base dos testids: container recebe o valor, botões `${base}-${id}`. */
  'data-testid'?: string;
}

/**
 * Seletor de tamanho do card do kanban (Detalhes / Normal / Compacto).
 * Construído sobre o `ViewToggle` compartilhado — "trocar a apresentação dos
 * mesmos dados" é o papel dele, e a regra do DS é estender o primitivo dono do
 * papel em vez de criar anatomia nova.
 *
 * Posição na toolbar: DEPOIS do toggle de visualização
 * (Operadores → Filtros → Ordenar → Lista/Kanban → Tamanho → sep → CTA), e só
 * quando a visualização ativa é Kanban. Em Atendimento, que não tem toggle de
 * visualização, entra logo depois do Ordenar.
 *
 * @example
 * ```tsx
 * {viewMode === 'kanban' && (
 *   <CardDensitySelector value={cardDensity} onChange={setCardDensity} data-testid="crm-card-density" />
 * )}
 * ```
 */
export const CardDensitySelector: React.FC<CardDensitySelectorProps> = ({
  value,
  onChange,
  className,
  options,
  'data-testid': dataTestId = 'card-density',
}) => {
  // A ordem de exibição é a da LISTA de densidades; o Record só dá o modo de
  // cada uma. Sem `options`, a lista é a canônica das três (`CARD_DENSITIES`).
  const shown = options ?? CARD_DENSITIES;
  const modes = shown.map((density) => DENSITY_MODES[density]);

  return (
    <ViewToggle<CardDensity>
      modes={modes}
      value={value}
      onChange={onChange}
      className={className}
      data-testid={dataTestId}
    />
  );
};

CardDensitySelector.displayName = 'CardDensitySelector';
